import { Configuration, ConfigurationRelease, Room } from '../../types/database';

interface ConfigurationMatrixProps {
  configurations: Configuration[];
  configReleases: ConfigurationRelease[];
  rooms: Room[];
  selectedConfigId: string | null;
  onSelectConfig: (configId: string) => void;
  onAssignRelease: (configId: string, phase: string, existingRelease?: ConfigurationRelease) => void;
  onPromoteRelease: (release: ConfigurationRelease, existingPhases: string[]) => void;
  onDeleteRelease: (releaseId: string) => void;
  readOnly?: boolean;
}

const PHASES = ['production', 'beta', 'alpha', 'backup'] as const;

export default function ConfigurationMatrix({
  configurations,
  configReleases,
  rooms,
  selectedConfigId,
  onSelectConfig,
  onAssignRelease,
  onPromoteRelease,
  onDeleteRelease,
  readOnly = false,
}: ConfigurationMatrixProps) {
  // Group config releases by configuration_id and phase
  const releaseMap = new Map<string, Map<string, ConfigurationRelease[]>>();
  for (const cr of configReleases) {
    if (!releaseMap.has(cr.configuration_id)) {
      releaseMap.set(cr.configuration_id, new Map());
    }
    const phaseMap = releaseMap.get(cr.configuration_id)!;
    if (!phaseMap.has(cr.phase)) {
      phaseMap.set(cr.phase, []);
    }
    phaseMap.get(cr.phase)!.push(cr);
  }

  // Map rooms by configuration_id
  const roomsByConfig = new Map<string, Room[]>();
  for (const room of rooms) {
    if (!room.configuration_id) continue;
    if (!roomsByConfig.has(room.configuration_id)) {
      roomsByConfig.set(room.configuration_id, []);
    }
    roomsByConfig.get(room.configuration_id)!.push(room);
  }

  const getExistingPhasesForExperience = (configId: string, experienceId: string): string[] => {
    const phaseMap = releaseMap.get(configId);
    if (!phaseMap) return [];
    const phases: string[] = [];
    phaseMap.forEach((releases, phase) => {
      if (releases.some((r) => r.experience_id === experienceId)) {
        phases.push(phase);
      }
    });
    return phases;
  };

  return (
    <div className="config-matrix">
      <div className="config-matrix-header">Configurations</div>
      <div className="config-matrix-scroll">
        <table className="config-matrix-table">
          <thead>
            <tr>
              <th>Configuration</th>
              {PHASES.map((phase) => (
                <th key={phase}>
                  <span className={`phase-badge phase-${phase}`}>{phase}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configurations.map((config) => {
              const phaseMap = releaseMap.get(config.id);
              const configRooms = roomsByConfig.get(config.id) || [];

              return (
                <tr
                  key={config.id}
                  className={`config-matrix-row ${selectedConfigId === config.id ? 'selected' : ''}`}
                  onClick={() => onSelectConfig(config.id)}
                >
                  <td>
                    <div className="config-name">{config.name}</div>
                    {configRooms.length > 0 && (
                      <div className="config-room-name">
                        {configRooms.map((r) => r.name).join(', ')}
                      </div>
                    )}
                  </td>
                  {PHASES.map((phase) => {
                    const releases = phaseMap?.get(phase) || [];
                    return (
                      <td key={phase} className="phase-cell">
                        {releases.length > 0 ? (
                          <div className="phase-cell-content">
                            {releases.map((cr) => (
                              <div key={cr.id} className="phase-release">
                                <span className="phase-release-name">
                                  {cr.experiences?.name || '?'}
                                </span>
                                <span className="phase-release-version">
                                  {cr.experience_release?.version || '?'}
                                </span>
                                {!readOnly && (
                                  <span className="phase-release-actions">
                                    <button
                                      className="phase-action-btn"
                                      title="Edit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onAssignRelease(config.id, phase, cr);
                                      }}
                                    >
                                      E
                                    </button>
                                    <button
                                      className="phase-action-btn"
                                      title="Promote"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const existingPhases = getExistingPhasesForExperience(config.id, cr.experience_id);
                                        onPromoteRelease(cr, existingPhases);
                                      }}
                                    >
                                      P
                                    </button>
                                    <button
                                      className="phase-action-btn"
                                      title="Delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (confirm(`Remove ${cr.experiences?.name || '?'} v${cr.experience_release?.version || '?'} from ${phase}?`)) {
                                          onDeleteRelease(cr.id);
                                        }
                                      }}
                                    >
                                      X
                                    </button>
                                  </span>
                                )}
                              </div>
                            ))}
                            {!readOnly && (
                              <button
                                className="phase-add-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAssignRelease(config.id, phase);
                                }}
                              >
                                +
                              </button>
                            )}
                          </div>
                        ) : !readOnly ? (
                          <button
                            className="phase-add-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAssignRelease(config.id, phase);
                            }}
                          >
                            + assign
                          </button>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
